import React, {useCallback, useMemo, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import Button from "../../base/button/button";
import {deepEqual} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import {MuiRFF, Yup} from '../../form';
import Api from "../../../../core/services/api/api";

const formNames = {
    title: 'title',
    description: 'description',
    policyId: 'policyId',
}


/**
 * The types of the product code type polities
 */
export const ProductCodeTypePolicies = {
    public: 1,
    partialShare: 2,
    private: 3,
}

const UpsertProductCodeTypeDialog = ({open, onClose, codeType, onUpsert}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const inEditMode = useMemo(() => !!codeType, [codeType]);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.title]: Yup.string().trim().required(ValidateMessages.required),
        [formNames.description]: Yup.string().trim(),
        ...(!inEditMode && {
            [formNames.policyId]: Yup.number().required(ValidateMessages.required),
        })
    }), [inEditMode]);

    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.title]: codeType?.title ?? '',
        [formNames.description]: codeType?.description ?? '',
        ...(!inEditMode && {
            [formNames.policyId]: ProductCodeTypePolicies.public.toString(),
        })
    }), [codeType?.title, codeType?.description, inEditMode]);

    /**
     * Updates the code type.
     * @type {(function(*): Promise<void>)}
     */
    const updateCodeType = useCallback(async (values) => {
        const forApi = {
            id: codeType?.id,
            title: values?.title,
            description: values?.description,
            isVisibleInPartInfo: codeType?.isVisibleInPartInfo,
            partSharePolicyId: codeType?.partSharePolicyId,
            updatedProperties: [
                "Title",
                "Description",
            ],
        }
        if (
            forApi.title === codeType?.title &&
            forApi.description === codeType?.description
        ) {
            onClose();
            return;
        }
        const response = await Api.updatePartCodeCategory(forApi)
        if (!isMounted())
            return
        if (response?.resultFlag) {
            onUpsert({
                id: forApi.id,
                title: forApi.title,
                description: forApi.description,
            })
        }
    }, [codeType, isMounted, onUpsert, onClose])

    /**
     * Creates a new code type with the provided values.
     * @type {(function(*): Promise<void>)}
     */
    const createCodeType = useCallback(async (values) => {
        const forApi = {
            title: values?.title,
            description: values?.description,
            partSharePolicyId: Number(values?.policyId),
        }
        const response = await Api.createPartCodeCategory(forApi)
        if (!isMounted())
            return
        if (response?.resultFlag) {
            onUpsert({
                id: response?.data?.id,
                title: forApi.title,
                description: forApi.description,
                partSharePolicyId: Number(values?.policyId),
                isVisibleInPartInfo: response?.data?.showInClientSide,
                isActive: response?.data?.isActive,
            })
        }
    }, [isMounted, onUpsert])

    /**
     * creates a new driver or updates the [selectedDriver] by calling the api regarding this action
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        let errors;
        if (!!inEditMode) {
            errors = await updateCodeType(values);
        } else {
            errors = await createCodeType(values);
        }
        if (!isMounted())
            return;
        setSubmitting(false);
        if (!errors) {
            onClose();
        }
        return errors;
    }, [inEditMode, isMounted, updateCodeType, createCodeType, onClose])

    return (
        <>
            <Dialog
                open={open}
                setOpen={onClose}
                maxWidth={'sm'}
            >
                <Row className='no-gutters align-items-center w-100 transition'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                            {
                                inEditMode
                                    ? 'Edit Selected Code Type'
                                    : 'Add a new Code Type'
                            }
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <Form
                        className='w-100'
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        render={({initialValues: formInitialValues, values}) => {
                            return (
                                <Row className='no-gutters justify-content-around w-100'>
                                    <Row className='no-gutters justify-content-around w-85'>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Title'}
                                                name={formNames.title}
                                                required={required[formNames.title]}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <MuiRFF.TextField
                                                label={'Description'}
                                                name={formNames.description}
                                                required={required[formNames.description]}
                                                multiline
                                                minRows={3}
                                            />
                                        </Col>
                                        {
                                            !inEditMode &&
                                            <Col xs={12} className={'mb-3'}>
                                                <MuiRFF.Radios
                                                    label={'Policy'}
                                                    name={formNames.policyId}
                                                    required={required[formNames.policyId]}
                                                    data={[
                                                        {
                                                            label: 'Public',
                                                            value: ProductCodeTypePolicies.public.toString(),
                                                        },
                                                        {
                                                            label: 'Partial Share',
                                                            value: ProductCodeTypePolicies.partialShare.toString(),
                                                        },
                                                        {
                                                            label: 'Private',
                                                            value: ProductCodeTypePolicies.private.toString(),
                                                        },
                                                    ]}
                                                />
                                            </Col>
                                        }
                                    </Row>
                                    <div className='my-3 w-100'/>
                                    <Divider className='divider'/>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || submitting}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => onClose(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {submitting ? 'loading...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || submitting || (inEditMode && deepEqual(formInitialValues, values))}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {submitting ? 'loading...' : inEditMode ? 'Update' : 'Add'}
                                        </Button>
                                    </Row>
                                </Row>
                            );
                        }}
                    />
                </Row>
            </Dialog>
        </>
    );
}

export default UpsertProductCodeTypeDialog;
